// src/components/FeedbackDialog.jsx
// Feedback (3.37.0): anyone can tell us what works and what doesn't. It's
// saved and emailed to admin@crazypixels.org. Super Admins also get "All
// feedback" here: every one that came in, to mark seen or done, and (3.37.3)
// reply right here when the person is happy to be contacted: the reply is
// emailed to them and kept under their feedback. Sent as the real
// signed-in person, even during View as. 3.38.0: the list can be filtered,
// sorted, paged, archived, deleted and downloaded (see AllFeedback).
import { useCallback, useEffect, useMemo, useState } from 'react';
import MenuDialog, { MD } from './MenuDialog';
import { PILL, PILL_ON, PILL_ROW } from '../theme/tabs';
import { sendFeedback, fetchFeedback, replyToFeedback, bulkFeedback, downloadSheet } from '../utils/api';
import ShowSelect from './ShowSelect';
import Pager, { usePager } from './Pager';
import { formatDate } from '../utils/format';
import { labelForTab } from '../utils/tabRoutes';
import { CURRENT_VERSION } from './WhatsNew';
import Loader from './Loader';
import Emo, { emoLabel } from './Emo';

const KINDS = [['idea', '💡 Idea'], ['problem', '🐞 Problem'], ['confusing', '😕 Something confusing'], ['praise', '👍 Praise'], ['other', '💬 Other']];
const STATUS = { new: { label: 'New', bg: 'var(--c-fff3e0)', color: 'var(--t-e65100)' }, seen: { label: 'Seen', bg: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)' }, done: { label: 'Done', bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' } };

export default function FeedbackDialog({ onClose, getToken, notify, confirm, activeTab, superAdmin, startOn = 'send', onCountChange }) {
  const [view, setView] = useState(superAdmin && startOn === 'all' ? 'all' : 'send');
  return (
    <MenuDialog id="feedback" icon="💬" title="Feedback" onClose={onClose} width={view === 'all' ? 980 : 640}
      sub={view === 'all' ? 'Everything people sent from the portal.' : 'Tell us what works, what doesn’t, or what would make the portal better.'}>
      {superAdmin && (
        <div style={{ ...PILL_ROW, marginBottom: 12 }} data-pills>
          <button onClick={() => setView('send')} style={{ ...PILL, ...(view === 'send' ? PILL_ON : {}) }} aria-pressed={view === 'send'}>Send feedback</button>
          <button onClick={() => setView('all')} style={{ ...PILL, ...(view === 'all' ? PILL_ON : {}) }} aria-pressed={view === 'all'}>All feedback</button>
        </div>
      )}
      {view === 'send'
        ? <SendForm getToken={getToken} notify={notify} activeTab={activeTab} onSent={onClose} />
        : <AllFeedback getToken={getToken} notify={notify} confirm={confirm} onCountChange={onCountChange} />}
    </MenuDialog>
  );
}

function SendForm({ getToken, notify, activeTab, onSent }) {
  const [kind, setKind] = useState('idea');
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [canContact, setCanContact] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const page = labelForTab(activeTab) || '';
  async function submit(e) {
    e.preventDefault();
    if (!message.trim()) { setError('Please write your feedback.'); return; }
    setBusy(true); setError('');
    try {
      await sendFeedback({ kind, rating: rating || null, message, canContact, page, version: CURRENT_VERSION, browser: navigator.userAgent }, getToken);
      notify({ tone: 'success', title: 'Thank you for your feedback', detail: 'It went to Fax Portal support.' });
      onSent();
    } catch (err) { setError(err.message); setBusy(false); }
  }
  return (
    <form onSubmit={submit}>
      <label style={{ ...MD.label, marginTop: 0 }}>What is it about?</label>
      <div style={PILL_ROW} role="radiogroup" aria-label="What is it about?">
        {KINDS.map(([k, l]) => <button type="button" key={k} role="radio" aria-checked={kind === k} onClick={() => setKind(k)} style={{ ...PILL, ...(kind === k ? PILL_ON : {}) }}>{emoLabel(l)}</button>)}
      </div>
      <label style={MD.label}>How is the portal working for you? <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
      <div role="radiogroup" aria-label="Rating, 1 to 5" style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button type="button" key={n} role="radio" aria-checked={rating === n} aria-label={`${n} of 5`} onClick={() => setRating(rating === n ? 0 : n)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 26, lineHeight: 1, padding: 2, color: n <= rating ? 'var(--t-ffa000)' : 'var(--t-cfd8dc)' }}>★</button>
        ))}
      </div>
      <label style={MD.label} htmlFor="fb-message">Your feedback</label>
      <textarea id="fb-message" value={message} onChange={e => setMessage(e.target.value)} maxLength={4000} rows={6} autoFocus
        placeholder="What happened, what you expected, or your idea. Please don't include patient details." style={{ ...MD.input, resize: 'vertical' }} />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginTop: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={canContact} onChange={e => setCanContact(e.target.checked)} /> You can contact me about this
      </label>
      <div style={{ ...MD.muted, marginTop: 10, fontSize: 12 }}>Sent with your name and email, your company, the page you're on{page ? ` (${page})` : ''} and the portal version (v{CURRENT_VERSION}).</div>
      {error && <div style={MD.error}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="submit" disabled={busy} style={{ ...MD.primary, opacity: busy ? 0.6 : 1 }}>{busy ? 'Sending…' : 'Send feedback'}</button>
      </div>
    </form>
  );
}

// All feedback (Super Admins). 3.38.0: Open / New / Done / Archived / All
// with counts; search, type, rating and client filters; sort; Show and
// pages; tick several to mark, archive or delete them at once; Download
// (.xlsx) of the ticked ones, or of everything the filters show.
const VIEWS = [['open', 'Open'], ['new', 'New'], ['done', 'Done'], ['archived', 'Archived'], ['all', 'All']];
const inView = (r, v) => (v === 'all' ? true : v === 'archived' ? !!r.archived
  : !r.archived && (v === 'open' ? r.status !== 'done' : r.status === v));
const SORTS = [
  ['newest', 'Newest first', (a, b) => cmp(b.createdAt, a.createdAt)],
  ['oldest', 'Oldest first', (a, b) => cmp(a.createdAt, b.createdAt)],
  ['rating_hi', 'Rating: high to low', (a, b) => (b.rating || 0) - (a.rating || 0) || cmp(b.createdAt, a.createdAt)],
  ['rating_lo', 'Rating: low to high', (a, b) => (a.rating || 9) - (b.rating || 9) || cmp(b.createdAt, a.createdAt)],
  ['kind', 'Type', (a, b) => cmp(a.kindLabel, b.kindLabel) || cmp(b.createdAt, a.createdAt)],
  ['client', 'Client', (a, b) => cmp(a.from?.accountName, b.from?.accountName) || cmp(b.createdAt, a.createdAt)],
  ['person', 'Person', (a, b) => cmp(who(a), who(b)) || cmp(b.createdAt, a.createdAt)],
];
function cmp(x, y) { const a = String(x || '').toLowerCase(), b = String(y || '').toLowerCase(); return a < b ? -1 : a > b ? 1 : 0; }
const who = f => f.from?.name || f.from?.email || '';
const FB_SHOW = ['10', '25', '50', '100', 'all'];

function AllFeedback({ getToken, notify, confirm, onCountChange }) {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState('open');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [rating, setRating] = useState('');
  const [client, setClient] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [show, setShow] = useState('25');
  const [picked, setPicked] = useState(() => new Set());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [replyFor, setReplyFor] = useState(null);     // the feedback id with the reply box open
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  async function sendReply(f) {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const { feedback } = await replyToFeedback(f.id, replyText, getToken);
      setRows(rs => rs.map(r => (r.id === f.id ? { ...r, ...feedback } : r)));
      setReplyFor(null); setReplyText('');
      notify({ tone: 'success', title: 'Reply sent', detail: `Emailed to ${f.from?.email}.` });
      onCountChange?.();
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t send the reply', detail: e.message }); }
    setReplying(false);
  }
  const load = useCallback(async () => {
    try { setRows((await fetchFeedback(getToken)).data || []); setError(''); }
    catch (e) { setError(e.message); setRows([]); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);
  // A new view or filter starts with nothing ticked
  useEffect(() => { setPicked(new Set()); }, [view, q, kind, rating, client]);

  const all = useMemo(() => rows || [], [rows]);
  const clients = useMemo(() => [...new Set(all.map(r => r.from?.accountName).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [all]);
  const filtered = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return all.filter(r => inView(r, view)
      && (!kind || r.kind === kind)
      && (!rating || (rating === 'none' ? !r.rating : String(r.rating) === rating))
      && (!client || r.from?.accountName === client)
      && (!words.length || (() => {
        const hay = [r.message, r.from?.name, r.from?.email, r.from?.accountName, r.page, ...(r.replies || []).map(x => x.text)].join(' ').toLowerCase();
        return words.every(w => hay.includes(w));
      })()));
  }, [all, view, q, kind, rating, client]);
  const sorted = useMemo(() => [...filtered].sort((SORTS.find(s => s[0] === sortBy) || SORTS[0])[2]), [filtered, sortBy]);
  const { pageRows: shown, pager } = usePager(sorted, show);
  if (!rows) return <Loader size="sm" />;

  const counts = Object.fromEntries(VIEWS.map(([k]) => [k, rows.filter(r => inView(r, k)).length]));
  const filtersOn = !!(q.trim() || kind || rating || client);
  const pickedRows = sorted.filter(r => picked.has(r.id));
  const pageAllPicked = shown.length > 0 && shown.every(r => picked.has(r.id));
  const toggle = id => setPicked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setPicked(s => { const n = new Set(s); shown.forEach(r => (pageAllPicked ? n.delete(r.id) : n.add(r.id))); return n; });

  // seen / done / reopen / archive / unarchive / delete, for one or many
  async function act(list, action) {
    if (!list.length) return;
    const n = list.length, many = n > 1;
    if (action === 'delete') {
      const ok = await confirm({
        title: many ? `Delete ${n} feedback for good?` : 'Delete this feedback for good?',
        message: `${many ? 'They' : 'It'} can't be brought back, and replies kept with ${many ? 'them' : 'it'} go too. To keep ${many ? 'them' : 'it'} out of the way instead, use Archive.`,
        confirmLabel: 'Delete', danger: true,
      });
      if (!ok) return;
    }
    setWorking(true);
    try {
      await bulkFeedback(list.map(r => r.id), action, getToken);
      const ids = new Set(list.map(r => r.id));
      setRows(rs => (action === 'delete' ? rs.filter(r => !ids.has(r.id)) : rs.map(r => (!ids.has(r.id) ? r
        : action === 'archive' ? { ...r, archived: true } : action === 'unarchive' ? { ...r, archived: false }
        : { ...r, status: action === 'reopen' ? 'seen' : action }))));
      setPicked(s => { const x = new Set(s); ids.forEach(id => x.delete(id)); return x; });
      const what = { seen: 'marked seen', done: 'marked done', reopen: 'reopened', archive: 'archived', unarchive: 'moved out of the archive', delete: 'deleted' }[action];
      if (many || action === 'archive' || action === 'unarchive' || action === 'delete') notify({ tone: 'success', title: `${many ? `${n} feedback` : 'Feedback'} ${what}` });
      onCountChange?.();
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t update it', detail: e.message }); }
    setWorking(false);
  }

  async function download() {
    const list = pickedRows.length ? pickedRows : sorted;
    try {
      await downloadSheet('Feedback', [
        { header: 'Date', get: r => formatDate(r.createdAt), width: 24 },
        { header: 'Status', get: r => (STATUS[r.status] || STATUS.new).label, width: 9 },
        { header: 'Archived', get: r => (r.archived ? 'Yes' : ''), width: 10 },
        { header: 'Type', get: r => r.kindLabel || '', width: 18 },
        { header: 'Rating', get: r => r.rating || '', width: 8 },
        { header: 'Feedback', get: r => r.message || '', width: 60 },
        { header: 'Name', get: r => r.from?.name || '', width: 20 },
        { header: 'Email', get: r => r.from?.email || '', width: 30 },
        { header: 'Client', get: r => r.from?.accountName || '', width: 20 },
        { header: 'Role', get: r => r.from?.roleLabel || '', width: 12 },
        { header: 'Page', get: r => r.page || '', width: 14 },
        { header: 'Version', get: r => r.version || '', width: 9 },
        { header: 'Can contact', get: r => (r.canContact === false ? 'No' : 'Yes'), width: 12 },
        { header: 'Replies', get: r => (r.replies || []).length || '', width: 9 },
        { header: 'Last reply', get: r => { const x = (r.replies || []).slice(-1)[0]; return x ? `${formatDate(x.at)} · ${x.byName || x.by}: ${x.text}` : ''; }, width: 60 },
      ], list, getToken);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  const small = { ...MD.secondary, padding: '4px 10px', fontSize: 12 };
  const select = { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' };
  const anyArchived = pickedRows.some(r => r.archived), anyActive = pickedRows.some(r => !r.archived);
  return (
    <>
      <div style={{ ...PILL_ROW, marginBottom: 10 }} role="tablist" aria-label="Which feedback">
        {VIEWS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)} style={{ ...PILL, padding: '4px 12px', fontSize: 13, ...(view === k ? PILL_ON : {}) }}>{l} ({counts[k]})</button>
        ))}
        <button onClick={download} disabled={!sorted.length} title={pickedRows.length ? 'Download the ticked feedback as an Excel file' : 'Download everything shown (all pages) as an Excel file'}
          style={{ ...small, marginLeft: 'auto', opacity: sorted.length ? 1 : 0.5 }}><Emo e="⬇" /> Download{pickedRows.length ? ` ${pickedRows.length} ticked` : ''} (.xlsx)</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search feedback, names, emails…" aria-label="Search feedback" style={{ ...select, flex: '1 1 200px', minWidth: 160 }} />
        <select value={kind} onChange={e => setKind(e.target.value)} style={select} aria-label="Type">
          <option value="">All types</option>
          {KINDS.map(([k, l]) => <option key={k} value={k}>{l.replace(/^\S+\s/, '')}</option>)}
          <option value="bug">Bug</option>
        </select>
        <select value={rating} onChange={e => setRating(e.target.value)} style={select} aria-label="Rating">
          <option value="">Any rating</option>
          {[5, 4, 3, 2, 1].map(n => <option key={n} value={String(n)}>{'★'.repeat(n)} ({n})</option>)}
          <option value="none">No rating</option>
        </select>
        {clients.length > 1 && (
          <select value={client} onChange={e => setClient(e.target.value)} style={select} aria-label="Client">
            <option value="">All clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={select} aria-label="Sort by">
          {SORTS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
        </select>
        <ShowSelect value={show} onChange={setShow} options={FB_SHOW} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }}
          labelStyle={{ fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 }} style={select} />
        {filtersOn && <button onClick={() => { setQ(''); setKind(''); setRating(''); setClient(''); }} style={{ ...small, border: 'none', background: 'none', textDecoration: 'underline' }}>Clear filters</button>}
      </div>
      {shown.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 10px', marginBottom: 10, borderRadius: 8, background: pickedRows.length ? 'var(--c-e0f2f1)' : 'var(--c-f9fafb)', fontSize: 13 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', color: 'var(--t-37474f)' }}>
            <input type="checkbox" checked={pageAllPicked} onChange={togglePage} /> {pickedRows.length ? `${pickedRows.length} ticked` : 'Tick all on this page'}
          </label>
          {pickedRows.length > 0 && (
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
              <button disabled={working} onClick={() => act(pickedRows, 'seen')} style={small}>Mark seen</button>
              <button disabled={working} onClick={() => act(pickedRows, 'done')} style={small}>✓ Done</button>
              {anyActive && <button disabled={working} onClick={() => act(pickedRows.filter(r => !r.archived), 'archive')} style={small}><Emo e="🗄" /> Archive</button>}
              {anyArchived && <button disabled={working} onClick={() => act(pickedRows.filter(r => r.archived), 'unarchive')} style={small}>Unarchive</button>}
              <button disabled={working} onClick={() => act(pickedRows, 'delete')} style={{ ...small, ...{ color: MD.danger.color, border: MD.danger.border } }}><Emo e="🗑" /> Delete</button>
              <button disabled={working} onClick={() => setPicked(new Set())} style={{ ...small, border: 'none', background: 'none', textDecoration: 'underline' }}>Clear</button>
            </span>
          )}
        </div>
      )}
      {error && <div style={MD.error}>{error}</div>}
      {!shown.length && <div style={{ ...MD.muted, padding: '10px 0' }}>{filtersOn ? 'No feedback matches these filters.' : view === 'archived' ? 'Nothing archived.' : 'Nothing here.'}</div>}
      {shown.map(f => {
        const st = STATUS[f.status] || STATUS.new;
        const on = picked.has(f.id);
        return (
          <div key={f.id} style={{ border: `1px solid ${on ? 'var(--c-80cbc4)' : 'var(--c-e0f2f1)'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 10, background: on ? 'var(--c-f1faf9)' : f.status === 'new' && !f.archived ? 'var(--c-fffdf7)' : 'var(--c-ffffff)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={on} onChange={() => toggle(f.id)} aria-label={`Tick feedback from ${who(f)}`} />
              <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 10, padding: '2px 8px', background: st.bg, color: st.color }}>{st.label}</span>
              {f.archived && <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 10, padding: '2px 8px', background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' }}>Archived</span>}
              <strong style={{ fontSize: 14, color: 'var(--t-263238)' }}>{f.kindLabel}</strong>
              {f.rating && <span style={{ color: 'var(--t-ffa000)', fontSize: 13 }} aria-label={`${f.rating} of 5`}>{'★'.repeat(f.rating)}<span style={{ color: 'var(--t-cfd8dc)' }}>{'★'.repeat(5 - f.rating)}</span></span>}
              <span style={{ ...MD.muted, fontSize: 12, marginLeft: 'auto' }}>{formatDate(f.createdAt)}</span>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--t-37474f)', margin: '8px 0' }}>{f.message}</div>
            {f.doing && <div style={{ fontSize: 13, color: 'var(--t-546e7a)', margin: '0 0 6px' }}><strong>What they were doing:</strong> {f.doing}</div>}
            {f.errors?.length > 0 && <div style={{ fontSize: 12, color: 'var(--t-78909c)', margin: '0 0 6px', wordBreak: 'break-word' }}><strong>Errors caught:</strong> {f.errors.join(' · ')}</div>}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--t-78909c)' }}>
              <span>{f.from?.name || f.from?.email} · {f.from?.email}{f.from?.accountName ? ` · ${f.from.accountName}` : ''}{f.from?.roleLabel ? ` · ${f.from.roleLabel}` : ''}{f.page ? ` · from ${f.page}` : ''}{f.version ? ` · v${f.version}` : ''}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {f.canContact && replyFor !== f.id && <button onClick={() => { setReplyFor(f.id); setReplyText(''); }} style={small}><Emo e="✉" /> Reply</button>}
                {f.status === 'new' && <button disabled={working} onClick={() => act([f], 'seen')} style={small}>Mark seen</button>}
                {f.status !== 'done'
                  ? <button disabled={working} onClick={() => act([f], 'done')} style={small}>✓ Done</button>
                  : <button disabled={working} onClick={() => act([f], 'reopen')} style={small}>Reopen</button>}
                {f.archived
                  ? <button disabled={working} onClick={() => act([f], 'unarchive')} style={small}>Unarchive</button>
                  : <button disabled={working} onClick={() => act([f], 'archive')} style={small} title="Keep it, out of Open, New and Done"><Emo e="🗄" /> Archive</button>}
                <button disabled={working} onClick={() => act([f], 'delete')} style={{ ...small, color: MD.danger.color, border: MD.danger.border }} aria-label="Delete this feedback" title="Delete for good"><Emo e="🗑" /></button>
              </span>
            </div>
            {!f.canContact && <div style={{ fontSize: 12, color: 'var(--t-90a4ae)', marginTop: 4 }}>They asked not to be contacted about this.</div>}
            {(f.replies || []).map((r, i) => (
              <div key={i} style={{ marginTop: 8, padding: '8px 12px', background: 'var(--c-f4f7f6)', borderLeft: '3px solid var(--c-00897b)', borderRadius: 6 }}>
                <div style={{ fontSize: 12, color: 'var(--t-78909c)', marginBottom: 2 }}>↩ Reply by {r.byName || r.by} · {formatDate(r.at)}{r.emailed ? ' · emailed' : ''}</div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, color: 'var(--t-37474f)' }}>{r.text}</div>
              </div>
            ))}
            {replyFor === f.id && (
              <div style={{ marginTop: 8 }}>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={4} maxLength={4000} autoFocus aria-label={`Reply to ${f.from?.name || f.from?.email}`}
                  placeholder={`Your reply to ${f.from?.name || f.from?.email}. It's emailed to ${f.from?.email}; their answer comes to support@crazypixels.org.`} style={{ ...MD.input, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                  <button onClick={() => { setReplyFor(null); setReplyText(''); }} disabled={replying} style={{ ...MD.secondary, padding: '6px 12px', fontSize: 12 }}>Cancel</button>
                  <button onClick={() => sendReply(f)} disabled={replying || !replyText.trim()} style={{ ...MD.primary, padding: '6px 14px', fontSize: 13, opacity: replying || !replyText.trim() ? 0.6 : 1 }}>{replying ? 'Sending…' : 'Send reply'}</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <Pager {...pager} label="feedback" />
    </>
  );
}
